import * as React from 'react';
import { Translate } from 'react-redux-i18n';
import { Dispatch } from 'react-redux';

import { StoreState } from '../../../../reducers';
import { Api } from '../../../../typings/api';
import {
	Wrapper,
	Header,
	ButtonCancel,
	DriveListWrapper,
	EmptyFolderLabel,
} from '../styles';
import Portal from '../../../Portal';
import HeaderLogo from '../../../../containers/MenuManager/Header/Logo';
import Breadcrumbs from '../Breadcrumbs';
import DriveItemList from '../../../DriveItemList';
import { ListDisplayType } from '../../../../containers/DriveItemList/constants';
import { getOrFetchDriveItemMeta, setBreadcrumbs, resetBreadcrumbs } from '../../../../containers/DriveItemList/actions';
import { DriveListState } from '../../../../containers/DriveItemList/reducer';
import UserAgentUtils from '../../../../utils/userAgent';
import useResetDrivePage from '../../../../utils/customHooks/useResetDrivePage';
import { getDriveItemIds, isFetchingState } from '../utils';

interface FromSelectionProps {
	selectionIds: number[];
	driveList: DriveListState;
	currentDriveId: number | undefined;
	sharedDirId: number;
	sourcesDirId: number;
	isValidResource: (item: Api.DriveItem) => boolean;
	onClose: () => void;
	onSelect: (items: Api.DriveItem[]) => void;
	dispatch: Dispatch<StoreState>;
	actionButtonColor?: string;
	className?: string;
}

const ROOT_FOLDER_ID = -1;

const getFolderResources = (
	currentDriveId: number | undefined,
	{ driveItems }: DriveListState,
	selectionIds: number[],
): Api.DriveItemResource[] => {
	if (!currentDriveId) {
		return [];
	}

	let resources: Api.DriveItemResource[];

	if (currentDriveId === ROOT_FOLDER_ID) {
		resources = selectionIds.map((id) => {
			// tempItem is used before actual item is fetched and can be filtered
			const tempItem = { id, type: 0 };
			return driveItems[id] || tempItem;
		});
	} else {
		const driveFolder = driveItems[currentDriveId];

		if (!driveFolder || !driveFolder.resources) {
			return [];
		}

		resources = driveFolder.resources;
	}

	return resources;
};

const FromSelection = ({
	selectionIds,
	driveList,
	currentDriveId,
	sharedDirId,
	sourcesDirId,
	isValidResource,
	onClose,
	onSelect,
	dispatch,
	actionButtonColor,
	className,
}: FromSelectionProps) => {
	const shouldResetDrivePage = useResetDrivePage([currentDriveId]);

	const getFolder = (id: number, label: string): void => {
		const isSharedDir = id === sharedDirId;

		if (id === sharedDirId) {
			label = 'messages.shared_with_me';
		}

		if (id === sourcesDirId) {
			label = 'messages.sources';
		}

		dispatch(setBreadcrumbs({ id, label }));

		if (id && id !== ROOT_FOLDER_ID && (!UserAgentUtils.isMobileApp || isSharedDir)) {
			dispatch(getOrFetchDriveItemMeta(id, true, true, isSharedDir));
		}
	};

	const handleBreadcrumbClick = (id: number, label: string): void => {
		getFolder(id, label);
	};

	const handleDriveItemClick = (item: Api.DriveItem): void => {
		const { type, id, label } = item;
		if (type === Api.DriveItemType.Directory) {
			getFolder(id, label);
		} else {
			onSelect([item]);
			onClose();
		}
	};

	React.useEffect(() => {
		dispatch(setBreadcrumbs({ id: ROOT_FOLDER_ID, label: 'messages.my_selection' }));

		return () => {
			dispatch(resetBreadcrumbs());
		};
	}, []);

	const { driveItems, driveItemsFetchStatus, breadcrumbs } = driveList;
	const isFetching = currentDriveId === ROOT_FOLDER_ID
		? false
		: isFetchingState(currentDriveId, driveItemsFetchStatus);
	const folderResources = getFolderResources(currentDriveId, driveList, selectionIds);
	const driveItemIds = getDriveItemIds(folderResources, driveList, isValidResource);
	const isEmptyFolder = !isFetching && driveItemIds.length < 1;

	return (
		<Portal parentNode={document.body}>
			<Wrapper className={className}>
				<Header>
					<HeaderLogo isDisabled={true} />
					<ButtonCancel
						type="button"
						onClick={onClose}
						color={actionButtonColor}
					>
						<Translate value="messages.cancel" />
					</ButtonCancel>
				</Header>
				<Breadcrumbs
					items={breadcrumbs}
					onItemClick={handleBreadcrumbClick}
				/>
				<DriveListWrapper>
					{isEmptyFolder && <EmptyFolderLabel value="messages.no_assets" />}
					<DriveItemList
						hasBottomBar={false}
						searchConfig={undefined}
						displayType={ListDisplayType.Row}
						storeDriveItems={driveItems}
						driveItemIdsToDisplay={driveItemIds}
						folderId={currentDriveId}
						sharedDirId={undefined}
						sourcesDirId={undefined}
						isPrivateDir={false}
						isFetching={isFetching}
						showUnreadBadge={false}
						hasActions={false}
						hasSelectionIcon={false}
						navigationItems={[]}
						shouldGetCustomTemplates={false}
						isEditMode={false}
						isEditButtonDisplayed={false}
						isDisplayTypeDropdownDisplayed={false}
						isLoading={false}
						isTheaterOpened={false}
						shouldResetPage={shouldResetDrivePage}
						onDriveItemClick={handleDriveItemClick}
						dispatch={dispatch}
					/>
				</DriveListWrapper>
			</Wrapper>
		</Portal>
	);
};

export default FromSelection;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/ResourceSelector/DrivePicker/FromSelection/index.tsx